import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { CalendarIcon } from '@/atoms/Icons/CalendarIcon'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { PillBadge } from '@/atoms/PillBadge'
import { Text } from '@/atoms/Text'
import { useDisclose } from '@/hooks/useDisclose'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import { getHoursMinutes } from '@/views/TicketCheckoutViews/utils'
import { GradientWrapper } from '@/views/TicketsLandingView/NewTicketsLandingView/components/GraidentWrapper'
import { ShareButton } from '@/views/TicketsLandingView/NewTicketsLandingView/components/ShareButton'
import {
  useGetReleaseActionType,
  useGetShareInfo,
  useInTheatersLabel,
} from '@/views/TicketsLandingView/NewTicketsLandingView/hooks'
import { CurrentTheatricalRelease } from '@/views/TicketsLandingView/NewTicketsLandingView/types'

interface Props {
  release: CurrentTheatricalRelease
  className?: string
}

export const ReleaseCard: FC<Props> = ({ release, className }: Props) => {
  const { locale } = useLocale()
  const { t } = useTranslate('tickets')
  const { isOpen: isTrailerOpen, onClose: onTrailerClose, onOpen: openTrailer } = useDisclose(false)

  const {
    theatricalSlug,
    angelKeyArt16x9Image,
    trailerUrl,
    title,
    duration,
    rating,
    hubPageLink,
    ticketPageLink,
    guildCodes,
  } = release

  const inTheatersLabel = useInTheatersLabel(release, locale)
  const actionType = useGetReleaseActionType(release)
  const shareInfo = useGetShareInfo(release)

  const pillList = useMemo(() => {
    return [
      {
        text: rating,
      },
      {
        text: !!duration && getHoursMinutes(duration / 60, locale),
      },
    ]
  }, [duration, locale, rating])

  return (
    <div className={classNames('w-full md:max-w-[480px] xl:max-w-[648px]', className)}>
      <GradientWrapper className="rounded-[14px] md:rounded-2xl lg:rounded-3xl">
        <button
          onClick={openTrailer}
          className={classNames(
            'block aspect-w-16 aspect-h-9 rounded-[14px] md:rounded-2xl lg:rounded-3xl w-full relative cursor-pointer border border-transparent overflow-hidden group focus-visible:border-black focus-visible:ring-[1px] focus-visible:ring-white',
            className,
          )}
        >
          <div>
            <Image
              className="transition-transform duration-[500ms] ease-in-out group-hover:scale-[1.02]"
              alt="Video player thumbnail"
              src={angelKeyArt16x9Image}
              fill
              sizes="(min-width: 1024px) 410px"
            />

            <div className="absolute inset-0 transition-colors duration-150 ease-in-out group-hover:bg-black/30 group-active:bg-black/50" />

            <div className="absolute left-1/2 top-1/2 flex h-[64px] w-[64px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 backdrop-blur-[48px] transition-all duration-150 ease-in-out group-hover:-translate-y-[calc(50%+2px)] group-hover:bg-white/50 group-focus-visible:-translate-y-[calc(50%+2px)] group-active:bg-white/20">
              <PlayIcon size={32} color="white" />
            </div>
          </div>
        </button>
      </GradientWrapper>

      <div className="mt-4 flex items-center gap-1 md:mt-6 md:gap-1.5">
        <CalendarIcon color="info-200" className="size-3 md:size-6" />
        <Text className="photon-caption-sm md:photon-caption-lg" color="info-200">
          {inTheatersLabel}
        </Text>
      </div>

      <Text weight="semibold" className="photon-title-sm md:photon-title-lg mt-3 block md:mt-4" color="white">
        {title}
      </Text>

      {pillList.length > 0 && (
        <div className="mt-2 flex items-center gap-1 md:mt-3">
          {pillList.map(({ text }) => {
            if (!text) return null

            return (
              <PillBadge key={text} size="sm" className="md:!pill-badge-md" variant="outlined">
                {text}
              </PillBadge>
            )
          })}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 md:mt-6 md:flex-row">
        {actionType === 'SHOWTIMES' && (
          <LinkButton size="sm" className="md:btn-md shrink-0 grow-1 md:grow-0" href={ticketPageLink} variant="white">
            <TicketIcon className="md:size-6" size={20} color="black" />
            {t('getShowtimes', 'Get Showtimes')}
          </LinkButton>
        )}

        {actionType === 'PRESALE' && (
          <LinkButton size="sm" className="md:btn-md shrink-0 grow-1 md:grow-0" href={ticketPageLink} variant="white">
            <TicketIcon className="md:size-6" size={20} color="black" />
            {t('getShowtimes', 'Get Showtimes')}
          </LinkButton>
        )}

        {actionType === 'GIVEAWAY' && (
          <LinkButton
            size="sm"
            className="md:btn-md grow-1 md:grow-0"
            href={`/giveaway/${theatricalSlug}`}
            variant="white"
          >
            {t('preOrderMovieTickets', 'Pre-Order Tickets')}
          </LinkButton>
        )}

        {actionType === 'GUILD_TICKETS' && guildCodes && (
          <LinkButton
            href={`/tickets/${theatricalSlug}?discounts=${guildCodes.join(',')}`}
            size="sm"
            className="md:btn-md grow-1 md:grow-0"
            variant="white"
          >
            <GuildIcon className="md:size-6" size={20} color1="black" />
            {t('claimTickets', 'Claim Tickets')}
          </LinkButton>
        )}

        <div className="flex gap-2">
          <LinkButton size="sm" className="md:btn-md grow-1 md:grow-0" href={hubPageLink} variant="offwhite">
            {t('learnMore', 'Learn More')}
          </LinkButton>

          <ShareButton
            shareIconClassName="hidden"
            buttonSize="sm"
            className="!btn-sm md:btn-md grow-1 md:grow-0"
            textClassName="!block"
            {...shareInfo}
          />
        </div>
      </div>

      {trailerUrl && (
        <YoutubeVideoModal handleClose={onTrailerClose} showModal={isTrailerOpen} youTubeUrl={trailerUrl} />
      )}
    </div>
  )
}
